import {Box, Flex, HStack, Image, Text, Textarea, useDisclosure, VStack} from '@chakra-ui/react';
import {TitledSection} from 'components/layouts/aside-layout';
import {colors, useCartContext} from 'components/providers';
import {useTranslation} from 'hooks';
import React, {useEffect, useState} from 'react';
import {ICartItem} from 'types';

import {useMetroStockValidation} from '../../hooks';
import DiscountLabel from '../DiscountLabel';
import {MetroProductDetailsModal} from '../metro-product-details-modal';
import {CartItemRow} from './CartItemRow';

export const CartItemsMetro = () => {
  const {
    isOpen: isOpenMetroProductModal,
    onOpen: onOpenMetroProductModal,
    onClose: onCloseMetroProductModal,
  } = useDisclosure();
  const {t} = useTranslation();
  const [selectedProductSlug, setSelectedProductSlug] = useState<string>();

  const {cartItems, cartProvider, deleteProduct, requiredProducts, editCartItem} = useCartContext();

  useMetroStockValidation();
  const [currentHref, setCurrentHref] = useState<string>();
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});

  useEffect(() => {
    const initialComments: Record<string, string> = {};
    cartItems.forEach((item) => {
      if (item.instructions) {
        initialComments[item.id] = item.instructions;
      }
    });
    setCommentTexts(initialComments);
  }, [cartItems]);

  const handleCommentChange = (itemId: string, value: string) => {
    setCommentTexts((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleCommentBlur = (item: ICartItem, value: string) => {
    const updatedCartItem = {
      ...item,
      instructions: value,
    };
    editCartItem(updatedCartItem);
  };

  const handleOpenProductModal = (product: ICartItem) => {
    setCurrentHref(window.location.href);
    setSelectedProductSlug(product.product.slug);
    onOpenMetroProductModal();
  };

  const handleCloseModal = () => {
    window.history.replaceState(null, '', currentHref);
    onCloseMetroProductModal();
  };
  return (
    <TitledSection>
      <VStack spacing={0} justifyContent="stretch" h="full">
        <Box overflowY="auto" flex="1" w="100%">
          {cartItems.map((item) => (
            <Box key={item.productId} p={4} onClick={() => handleOpenProductModal(item)}>
              <HStack spacing={3} align="stretch" minH="100px">
                <Box
                  w="100px"
                  h="100px"
                  flexShrink={0}
                  borderColor="gray.300"
                  borderWidth={1}
                  borderRadius="10px"
                  position="relative"
                >
                  <DiscountLabel oldPrice={item.product.old_price} price={item.product.price} />
                  <Image
                    src={item.product?.product_img_for_detail_thumbnail || ''}
                    alt={item.product?.name || ''}
                    borderRadius="8px"
                    fallback={
                      <Image
                        src="/food_pattern.webp"
                        borderRadius="8px"
                        alt="fallback"
                        objectFit="cover"
                        w="100%"
                        h="100%"
                      />
                    }
                    w="100%"
                    h="100%"
                    objectFit="contain"
                  />
                </Box>

                <Flex flex={1} direction="column">
                  <CartItemRow item={item} provider={cartProvider} onDelete={deleteProduct} />
                </Flex>
              </HStack>

              {/* Comment input for metro products - full width */}

              <Textarea
                value={commentTexts[item.id] ?? item.instructions ?? ''}
                onChange={(e) => handleCommentChange(item.id, e.target.value)}
                onBlur={(e) => handleCommentBlur(item, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder={t('commentForPicker')}
                maxLength={300}
                fontSize="14px"
                rows={1}
                mt={2}
                borderRadius={5}
              />
            </Box>
          ))}
          {requiredProducts.map((item, index) => (
            <Box
              key={index}
              borderBottom={index < requiredProducts.length - 1 ? '1px solid' : 'none'}
              borderColor={colors.borderGray}
              p={4}
            >
              <HStack spacing={3} align="flex-start">
                <Box w="60px" h="60px" flexShrink={0}>
                  <Image
                    src={item.product_img_for_list_thumbnail}
                    alt={item.name}
                    borderRadius="8px"
                    fallbackSrc="/food_pattern.webp"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                </Box>

                <Flex flex={1} direction="column">
                  <Text fontSize="14px" fontWeight="500" lineHeight="1.3" color="gray.800" noOfLines={2}>
                    {item.name}
                  </Text>

                  {item.measurement_display && (
                    <Text color={colors.textGray} fontSize="12px" mt={1}>
                      {item.measurement_display}
                    </Text>
                  )}

                  <HStack justify="space-between" align="center" mt={3}>
                    <Text fontSize="16px" fontWeight="600" color="gray.800">
                      1
                    </Text>
                    <Text fontSize="16px" fontWeight="600" color="gray.800">
                      {item.price} {t('CURRENCY')}
                    </Text>
                  </HStack>
                </Flex>
              </HStack>
            </Box>
          ))}
        </Box>
      </VStack>

      {isOpenMetroProductModal && selectedProductSlug && (
        <MetroProductDetailsModal
          isOpen={isOpenMetroProductModal}
          onClose={handleCloseModal}
          productSlug={selectedProductSlug}
          provider={cartProvider}
        />
      )}
    </TitledSection>
  );
};
